import { audioEndMs, isStill, segmentLengthMs, segmentStarts, type AudioTrack, type EditProject } from '@airpost/shared';

/**
 * The edit on the timeline: the project's milliseconds become the timeline's seconds (rows of actions, as the
 * timeline library reads them) and a finished drag comes back as a project; the timecode and the zoom levels of
 * the ruler. Pure: the page does the drawing.
 */
export const FPS = 25;
/** Seconds per major tick of the ruler, from the widest view to the closest. */
export const SCALES: readonly number[] = [60, 30, 10, 5, 2, 1, 0.5, 0.2];
/** Pixels per major tick. */
export const SCALE_WIDTH = 120;
/** The ruler's left margin, in pixels. */
export const START_LEFT = 10;
/** The ruler's height plus the gap under it, as the library lays them out. */
export const RULER_PX = 42;

export const ROW = { overlay: 'v2', video: 'v1', straps: 't1', text: 't2', vo: 'a1', music: 'a2', sound: 'a3' } as const;
export type RowKey = keyof typeof ROW;
/** Top to bottom: the pictures over the sequence, the pieces, the lower thirds, the texts, the voice-over, the music, the pieces' separated sound. */
export const ROW_KEYS: readonly RowKey[] = ['overlay', 'video', 'straps', 'text', 'vo', 'music', 'sound'];
export const ROW_HEIGHT: Record<RowKey, number> = { overlay: 38, video: 66, straps: 32, text: 36, vo: 48, music: 48, sound: 48 };

/** An action as the timeline library reads it (seconds). */
export interface TlAction {
  id: string;
  start: number;
  end: number;
  effectId: string;
  selected?: boolean;
  flexible?: boolean;
  movable?: boolean;
  minStart?: number;
  maxEnd?: number;
}
export interface TlRow {
  id: string;
  actions: TlAction[];
  rowHeight?: number;
  classNames?: string[];
}
export interface TlMeta {
  canEdit: boolean;
}
/** What the inspector shows: an item, a track (its takes or beds, its texts, its pictures), the mixer, or the format. */
export type Selection =
  | { kind: 'segment'; id: string }
  | { kind: 'straps' }
  | { kind: 'strap'; id: string }
  | { kind: 'texts' }
  | { kind: 'text'; id: string }
  | { kind: 'overlays' }
  | { kind: 'overlay'; id: string }
  | { kind: 'track'; track: AudioTrack }
  | { kind: 'audio'; id: string }
  | { kind: 'dip'; id: string }
  | { kind: 'mix' }
  | { kind: 'format' };

export const segmentActionId = (id: string): string => `g:${id}`;
export const segmentIdOf = (actionId: string): string | null => (actionId.startsWith('g:') ? actionId.slice(2) : null);
export const strapActionId = (id: string): string => `s:${id}`;
export const strapIdOf = (actionId: string): string | null => (actionId.startsWith('s:') ? actionId.slice(2) : null);
export const audioActionId = (id: string): string => `a:${id}`;
export const audioIdOf = (actionId: string): string | null => (actionId.startsWith('a:') ? actionId.slice(2) : null);
export const dipActionId = (id: string): string => `d:${id}`;
export const dipIdOf = (actionId: string): string | null => (actionId.startsWith('d:') ? actionId.slice(2) : null);
export const textActionId = (id: string): string => `x:${id}`;
export const textIdOf = (actionId: string): string | null => (actionId.startsWith('x:') ? actionId.slice(2) : null);
export const overlayActionId = (id: string): string => `o:${id}`;
export const overlayIdOf = (actionId: string): string | null => (actionId.startsWith('o:') ? actionId.slice(2) : null);

/**
 * The project as rows of the timeline: the pictures on V2, the pieces on V1, the straps, the texts, the voice-over
 * clips, the music clips with their dips, the pieces' separated sound. Nothing on a locked track moves.
 */
export function rowsOf(p: EditProject, meta: TlMeta, sel: Selection): TlRow[] {
  const locked = new Set<string>(p.lanes?.locked ?? []);
  const hidden = new Set<string>(p.lanes?.hidden ?? []);
  const rowClass = (k: RowKey) => ['ve-row', `ve-row-${k === 'overlay' ? 'overlay' : k === 'video' ? 'video' : k === 'straps' ? 'straps' : k === 'text' ? 'text' : k}`, ...(locked.has(k) ? ['ve-row-locked'] : []), ...(hidden.has(k) ? ['ve-row-hidden'] : [])];
  const free = meta.canEdit;
  const freeOn = (k: RowKey) => free && !locked.has(k);
  const starts = segmentStarts(p);
  const pieces = p.segments.map<TlAction>((s, i) => ({
    id: segmentActionId(s.id),
    start: starts[i]! / 1000,
    end: (starts[i]! + segmentLengthMs(s)) / 1000,
    effectId: 'clip',
    movable: freeOn('video') && p.segments.length > 1,
    flexible: freeOn('video'),
    minStart: 0,
    selected: sel.kind === 'segment' && sel.id === s.id,
  }));
  const straps = p.straps.map<TlAction>((s) => ({
    id: strapActionId(s.id),
    start: s.atMs / 1000,
    end: (s.atMs + s.durationMs) / 1000,
    effectId: 'strap',
    movable: freeOn('straps'),
    flexible: freeOn('straps'),
    minStart: 0,
    selected: sel.kind === 'strap' && sel.id === s.id,
  }));
  const clips = (track: AudioTrack) =>
    p.audio
      .filter((c) => c.track === track)
      .map<TlAction>((c) => ({ id: audioActionId(c.id), start: c.atMs / 1000, end: audioEndMs(c) / 1000, effectId: track, movable: freeOn(track), flexible: freeOn(track), minStart: 0, selected: sel.kind === 'audio' && sel.id === c.id }));
  // the dips sit over the music, each dragged and stretched on its own
  const dips = p.dips.map<TlAction>((d) => ({ id: dipActionId(d.id), start: d.fromMs / 1000, end: d.toMs / 1000, effectId: 'dip', movable: freeOn('music'), flexible: freeOn('music'), minStart: 0, selected: sel.kind === 'dip' && sel.id === d.id }));
  const texts = p.texts.map<TlAction>((x) => ({ id: textActionId(x.id), start: x.atMs / 1000, end: (x.atMs + x.durationMs) / 1000, effectId: 'text', movable: freeOn('text'), flexible: freeOn('text'), minStart: 0, selected: sel.kind === 'text' && sel.id === x.id }));
  const overlays = p.overlays.map<TlAction>((o) => ({ id: overlayActionId(o.id), start: o.atMs / 1000, end: (o.atMs + o.durationMs) / 1000, effectId: 'overlay', movable: freeOn('overlay'), flexible: freeOn('overlay'), minStart: 0, selected: sel.kind === 'overlay' && sel.id === o.id }));
  return [
    { id: ROW.overlay, actions: overlays, rowHeight: ROW_HEIGHT.overlay, classNames: rowClass('overlay') },
    { id: ROW.video, actions: pieces, rowHeight: ROW_HEIGHT.video, classNames: rowClass('video') },
    { id: ROW.straps, actions: straps, rowHeight: ROW_HEIGHT.straps, classNames: rowClass('straps') },
    { id: ROW.text, actions: texts, rowHeight: ROW_HEIGHT.text, classNames: rowClass('text') },
    { id: ROW.vo, actions: clips('vo'), rowHeight: ROW_HEIGHT.vo, classNames: rowClass('vo') },
    { id: ROW.music, actions: [...clips('music'), ...dips], rowHeight: ROW_HEIGHT.music, classNames: rowClass('music') },
    { id: ROW.sound, actions: clips('sound'), rowHeight: ROW_HEIGHT.sound, classNames: rowClass('sound') },
  ];
}

/**
 * The project after a drag on the timeline. A piece whose two ends moved together was moved: the pieces are put
 * in the order of their middles (the dragged one at its new place; they always sit one after the other). A piece
 * whose one end moved was trimmed: its in or out point moves by as much of its source (at its speed); a frozen frame
 * or a photo only gets longer or shorter. An audio clip moved as a whole changes its place; one trimmed at its start
 * moves its place and in point together, at its end its out point. Each strap's, text's and picture's time and
 * length, and each dip's range, come straight back.
 */
export function projectFromRows(p: EditProject, rows: TlRow[]): EditProject {
  const by = new Map<string, TlAction>();
  for (const r of rows) for (const a of r.actions) by.set(a.id, a);
  const ms = (s: number) => Math.round(s * 1000);
  const starts = segmentStarts(p);
  const placed = p.segments.map((s, i) => {
    const a = by.get(segmentActionId(s.id));
    const oldStart = starts[i]!;
    const oldEnd = oldStart + segmentLengthMs(s);
    const oldMid = (oldStart + oldEnd) / 2;
    if (!a) return { seg: s, order: oldMid };
    const dStart = ms(a.start) - oldStart;
    const dEnd = ms(a.end) - oldEnd;
    if (Math.abs(dStart - dEnd) <= 1) return { seg: s, order: oldMid + dStart };
    if (isStill(s)) return { seg: { ...s, outMs: s.inMs + Math.max(40, ms(a.end - a.start)) }, order: oldMid };
    return { seg: { ...s, inMs: s.inMs + Math.round(dStart * s.speed), outMs: s.outMs + Math.round(dEnd * s.speed) }, order: oldMid };
  });
  placed.sort((x, y) => x.order - y.order);
  return {
    ...p,
    segments: placed.map((x) => x.seg),
    straps: p.straps.map((s) => {
      const a = by.get(strapActionId(s.id));
      return a ? { ...s, atMs: ms(a.start), durationMs: Math.max(500, ms(a.end - a.start)) } : s;
    }),
    audio: p.audio.map((c) => {
      const a = by.get(audioActionId(c.id));
      if (!a) return c;
      const dStart = ms(a.start) - c.atMs;
      const dEnd = ms(a.end) - audioEndMs(c);
      if (Math.abs(dStart - dEnd) <= 1) return { ...c, atMs: c.atMs + dStart };
      return { ...c, atMs: c.atMs + dStart, inMs: Math.max(0, c.inMs + dStart), outMs: c.outMs + dEnd };
    }),
    dips: p.dips.map((d) => {
      const a = by.get(dipActionId(d.id));
      return a ? { ...d, fromMs: ms(a.start), toMs: Math.max(ms(a.start) + 500, ms(a.end)) } : d;
    }),
    texts: p.texts.map((x) => {
      const a = by.get(textActionId(x.id));
      return a ? { ...x, atMs: ms(a.start), durationMs: Math.max(200, ms(a.end - a.start)) } : x;
    }),
    overlays: p.overlays.map((o) => {
      const a = by.get(overlayActionId(o.id));
      return a ? { ...o, atMs: ms(a.start), durationMs: Math.max(200, ms(a.end - a.start)) } : o;
    }),
  };
}

/** What a click on an action selects. */
export function selectionOf(actionId: string): Selection | null {
  const a = audioIdOf(actionId);
  if (a) return { kind: 'audio', id: a };
  const d = dipIdOf(actionId);
  if (d) return { kind: 'dip', id: d };
  const x = textIdOf(actionId);
  if (x) return { kind: 'text', id: x };
  const o = overlayIdOf(actionId);
  if (o) return { kind: 'overlay', id: o };
  const g = segmentIdOf(actionId);
  if (g) return { kind: 'segment', id: g };
  const s = strapIdOf(actionId);
  return s ? { kind: 'strap', id: s } : null;
}

/** What a click on a track's head selects. */
export function selectionOfRow(rowId: string, firstSegmentId: string | null): Selection {
  if (rowId === ROW.straps) return { kind: 'straps' };
  if (rowId === ROW.text) return { kind: 'texts' };
  if (rowId === ROW.overlay) return { kind: 'overlays' };
  if (rowId === ROW.vo) return { kind: 'track', track: 'vo' };
  if (rowId === ROW.music) return { kind: 'track', track: 'music' };
  if (rowId === ROW.sound) return { kind: 'track', track: 'sound' };
  return firstSegmentId ? { kind: 'segment', id: firstSegmentId } : { kind: 'mix' };
}

/** The track a selection sits on (the head to light up, and the track C cuts). */
export function rowOfSelection(sel: Selection, p: Pick<EditProject, 'audio'>): RowKey | null {
  if (sel.kind === 'segment') return 'video';
  if (sel.kind === 'straps' || sel.kind === 'strap') return 'straps';
  if (sel.kind === 'texts' || sel.kind === 'text') return 'text';
  if (sel.kind === 'overlays' || sel.kind === 'overlay') return 'overlay';
  if (sel.kind === 'track') return sel.track;
  if (sel.kind === 'dip') return 'music';
  if (sel.kind === 'audio') return p.audio.find((c) => c.id === sel.id)?.track ?? null;
  return null;
}

/** How far the pointer may wander between press and release for it still to be a click (a shaky hand), in pixels. */
export const CLICK_SLOP_PX = 4;

/** Whether a press on the timeline travelled before its release: a move or a trim, so not a click that seeks. */
export const pressTravelled = (down: { x: number; y: number }, up: { x: number; y: number }): boolean => Math.abs(up.x - down.x) > CLICK_SLOP_PX || Math.abs(up.y - down.y) > CLICK_SLOP_PX;

export const pxPerSecond = (scale: number): number => SCALE_WIDTH / scale;

/** The closest zoom level at which the whole sequence still fits the given width. */
export function fitScaleIndex(totalS: number, widthPx: number): number {
  for (let i = SCALES.length - 1; i >= 0; i--) if (START_LEFT + (totalS / SCALES[i]!) * SCALE_WIDTH + SCALE_WIDTH <= widthPx) return i;
  return 0;
}

const pad = (n: number) => String(n).padStart(2, '0');

/** HH:MM:SS:FF at 25 frames a second. */
export function timecode(ms: number, fps = FPS): string {
  const total = Math.max(0, Math.round(ms));
  const s = Math.floor(total / 1000);
  const f = Math.floor(((total % 1000) / 1000) * fps);
  return `${pad(Math.floor(s / 3600))}:${pad(Math.floor((s % 3600) / 60))}:${pad(s % 60)}:${pad(f)}`;
}

/** The ruler's label at a tick: mm:ss, with tenths when the ticks are closer than a second. */
export function scaleLabel(tickS: number, scale: number): string {
  const s = Math.floor(tickS + 1e-6);
  const base = `${pad(Math.floor(s / 60))}:${pad(s % 60)}`;
  return scale < 1 ? `${base}.${Math.round((tickS - s) * 10) % 10}` : base;
}

/** A level as decibels for a slider (−∞ shown as −60), and back. */
export const toDb = (gain: number): number => (gain <= 0.001 ? -60 : Math.round(20 * Math.log10(gain) * 10) / 10);
export const fromDb = (db: number): number => (db <= -59.9 ? 0 : Math.round(10 ** (db / 20) * 1000) / 1000);
